import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material'
import { useNotify, useRefresh } from 'react-admin'
import { supabase } from '../supabaseClient'

interface Props {
  open: boolean
  onClose: () => void
  propertyId: string
  currentTitle: string
}

const MAX_LENGTH = 200

export const EditTitleModal: React.FC<Props> = ({ open, onClose, propertyId, currentTitle }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [value, setValue] = useState(currentTitle ?? '')
  const [saving, setSaving] = useState(false)

  const newTitle = value.trim()
  const valid = newTitle.length > 0 && newTitle.length <= MAX_LENGTH
  const changed = valid && newTitle !== currentTitle

  async function confirm() {
    setSaving(true)
    const { data, error } = await supabase.rpc('update_property_title', {
      p_property_id: propertyId,
      p_new_title: newTitle,
    })
    setSaving(false)
    const result = data as { success?: boolean; error?: string } | null
    if (error || result?.success === false) {
      notify(error?.message || result?.error || 'Błąd zapisu nazwy', { type: 'error' })
      return
    }
    notify('Zaktualizowano nazwę lokalu', { type: 'success' })
    refresh()
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edycja nazwy — {currentTitle}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Nowa nazwa"
          fullWidth
          margin="dense"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!value && !valid}
          helperText={
            !!value && !valid
              ? `Nazwa nie może być pusta ani dłuższa niż ${MAX_LENGTH} znaków`
              : ' '
          }
          inputProps={{ maxLength: MAX_LENGTH, 'data-testid': 'title-input' }}
        />

        {/* Confirm — wpływ na widget przed akceptacją */}
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Nazwa jest widoczna dla klientów w widgecie (nagłówek karty lokalu i kafelek na liście).
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Nazwa:</Typography>
            <Typography variant="body2">
              {currentTitle} → <strong>{valid ? newTitle : '—'}</strong>
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Lokale na liście są sortowane po nazwie — zmiana może przesunąć lokal w kolejności.
            Wcześniej udostępnione linki zbudowane ze starej nazwy przestaną działać.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Nie zmienia oznaczenia lokalu raportowanego do dane.gov.pl (to osobne pole).
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anuluj</Button>
        <Button variant="contained" onClick={confirm} disabled={!changed || saving}>
          {saving ? 'Zapisywanie…' : 'Potwierdź zmianę'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
